import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { ACTIVE_FILTER_OPTIONS, TRACKING_MODES, WarehousingMasterType, masterDataMap, ownPhysicalWarehouses, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItemSummary, WarehousingMasterData, WarehousingSearchFilters } from '../warehousing.model';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingSearchPanelComponent } from '../warehousing-search-panel.component';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel, itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-items',
  standalone: true,
  imports: [AuditDirectoryCellComponent,
    CommonModule, ReactiveFormsModule, RouterModule, AutoCompleteModule, BreadcrumbModule, ButtonModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule, TableModule,
    TagModule, WarehousingPageHeaderComponent, WarehousingSearchPanelComponent
  ],
  templateUrl: './warehousing-items.component.html',
  styleUrls: ['./warehousing-items.component.css']
})
export class WarehousingItemsComponent implements OnInit {
  readonly itemSizeLabel = itemSizeLabel;
  private fb = inject(FormBuilder);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);

  isLoading = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Items');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  items: WarehousingItemSummary[] = [];
  totalRecords = 0;
  first = 0;
  rows = 12;
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  masterData = masterDataMap();
  trackingModes = TRACKING_MODES;
  activeFilterOptions = ACTIVE_FILTER_OPTIONS;
  itemOptions: Array<WarehousingItemSummary & { displayLabel: string }> = [];
  private itemSearchRequest = 0;
  private referenceDataLoaded = false;
  private failedThumbnailIds = new Set<number>();

  searchForm = this.fb.group({
    keyword: [''],
    item: [null as (WarehousingItemSummary & { displayLabel: string }) | null],
    warehouseId: [null as number | null],
    crmClientId: [null as number | null],
    crmAccountId: [null as number | null],
    categoryId: [null as number | null],
    brandId: [null as number | null],
    subBrandId: [null as number | null],
    trackingMode: [null as string | null],
    activeStatus: [null as string | null]
  });

  ngOnInit(): void {
    this.registerCascadeResets();
    this.loadReferenceData();
  }

  loadItems(event?: TableLazyLoadEvent): void {
    if (!this.referenceDataLoaded) {
      return;
    }
    if (event) {
      this.first = Number(event.first || 0);
      this.rows = Number(event.rows || this.rows);
    }
    this.isLoading = true;
    this.warehousingService.getItemSummaries(this.searchFilters(), Math.floor(this.first / this.rows), this.rows).subscribe({
      next: response => {
        this.items = response.data?.items || [];
        this.totalRecords = response.data?.totalRecords || 0;
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  applySearch(): void {
    this.first = 0;
    this.failedThumbnailIds.clear();
    this.loadItems();
  }

  clearSearch(): void {
    this.searchForm.reset({
      keyword: '',
      item: null,
      warehouseId: null,
      crmClientId: null,
      crmAccountId: null,
      categoryId: null,
      brandId: null,
      subBrandId: null,
      trackingMode: null,
      activeStatus: null
    });
    this.first = 0;
    this.failedThumbnailIds.clear();
    this.itemOptions = [];
    this.itemSearchRequest++;
    this.loadItems();
  }

  searchItemOptions(event: { query: string }): void {
    const request = ++this.itemSearchRequest;
    const keyword = event.query.trim();
    if (!keyword) { this.itemOptions = []; return; }
    this.warehousingService.getItemSummaries({ keyword }, 0, 20).subscribe({
      next: response => {
        if (request === this.itemSearchRequest) {
          this.itemOptions = (response.data?.items || []).map(item => ({ ...item, displayLabel: itemIdentityLabel(item) }));
        }
      },
      error: () => { if (request === this.itemSearchRequest) this.itemOptions = []; }
    });
  }

  markThumbnailFailed(itemId: number): void {
    this.failedThumbnailIds.add(itemId);
  }

  isThumbnailFailed(itemId: number): boolean {
    return this.failedThumbnailIds.has(itemId);
  }

  private loadReferenceData(): void {
    this.isLoading = true;
    forkJoin({
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      categories: this.warehousingService.getMasterData('CATEGORY'),
      brands: this.warehousingService.getMasterData('BRAND'),
      subBrands: this.warehousingService.getMasterData('SUB_BRAND'),
      tenantUoms: this.warehousingService.getMasterData('TENANT_UOM'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE')
    }).subscribe({
      next: response => {
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.masterData.CATEGORY = response.categories.data || [];
        this.masterData.BRAND = response.brands.data || [];
        this.masterData.SUB_BRAND = response.subBrands.data || [];
        this.masterData.TENANT_UOM = response.tenantUoms.data || [];
        this.masterData.WAREHOUSE = ownPhysicalWarehouses(response.warehouses.data || []);
        this.referenceDataLoaded = true;
        this.loadItems();
      },
      error: () => this.isLoading = false
    });
  }

  clientName(id?: number): string {
    return this.clients.find(client => client.id === id)?.companyName || '-';
  }

  accountName(id?: number): string {
    return this.accounts.find(account => account.id === id)?.companyName || '-';
  }

  filteredAccounts(clientId: number | null | undefined): CrmAccount[] {
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }

  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }

  categoryOptions(): WarehousingMasterData[] {
    const clientId = this.searchForm.value.crmClientId;
    const accountId = this.searchForm.value.crmAccountId;
    const sourceRows = this.masterData.CATEGORY.filter(row =>
      (!row.crmClientId || !clientId || row.crmClientId === clientId) &&
      (!row.crmAccountId || !accountId || row.crmAccountId === accountId)
    );
    return this.categoryTreeOptions(sourceRows);
  }

  brandOptions(): WarehousingMasterData[] {
    const categoryId = this.searchForm.value.categoryId;
    return this.masterData.BRAND.filter(row => !categoryId || row.parentId === categoryId || row.categoryId === categoryId);
  }

  subBrandOptions(): WarehousingMasterData[] {
    const brandId = this.searchForm.value.brandId;
    return this.masterData.SUB_BRAND.filter(row => !brandId || row.parentId === brandId || row.brandId === brandId);
  }

  hierarchyLabel(item: WarehousingItemSummary): string {
    return item.hierarchyPath || [
      item.categoryName || this.masterName('CATEGORY', item.categoryId),
      item.brandName || this.masterName('BRAND', item.brandId),
      item.subBrandName || this.masterName('SUB_BRAND', item.subBrandId)
    ].filter(value => value && value !== '-').join(' > ') || '-';
  }

  private searchFilters(): WarehousingSearchFilters {
    const { item, ...filters } = this.searchForm.getRawValue();
    return { ...filters, itemId: item?.id || null };
  }

  private registerCascadeResets(): void {
    this.searchForm.get('crmClientId')?.valueChanges.subscribe(clientId => {
      const accountId = this.searchForm.value.crmAccountId;
      if (accountId && !this.filteredAccounts(clientId).some(account => account.id === accountId)) {
        this.searchForm.patchValue({ crmAccountId: null }, { emitEvent: false });
      }
      this.resetHierarchy();
    });
    this.searchForm.get('crmAccountId')?.valueChanges.subscribe(() => this.resetHierarchy());
    this.searchForm.get('categoryId')?.valueChanges.subscribe(() => {
      this.searchForm.patchValue({ brandId: null, subBrandId: null }, { emitEvent: false });
    });
    this.searchForm.get('brandId')?.valueChanges.subscribe(() => {
      this.searchForm.patchValue({ subBrandId: null }, { emitEvent: false });
    });
  }

  private resetHierarchy(): void {
    this.searchForm.patchValue({ categoryId: null, brandId: null, subBrandId: null }, { emitEvent: false });
  }

  private categoryTreeOptions(rows: WarehousingMasterData[]): WarehousingMasterData[] {
    const byParent = new Map<number, WarehousingMasterData[]>();
    const ids = new Set(rows.map(row => row.id).filter((id): id is number => !!id));
    rows.forEach(row => {
      if (!row.parentId) return;
      const children = byParent.get(row.parentId) || [];
      children.push(row);
      byParent.set(row.parentId, children);
    });
    const output: WarehousingMasterData[] = [];
    const append = (row: WarehousingMasterData, depth: number): void => {
      output.push({ ...row, name: `${'-- '.repeat(depth)}${row.name}` });
      (row.id ? byParent.get(row.id) || [] : [])
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .forEach(child => append(child, depth + 1));
    };
    rows
      .filter(row => !row.parentId || !ids.has(row.parentId))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach(row => append(row, 0));
    return output;
  }
}
